import { useState, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import type { UserPaymentDetailsDto } from '../../../api/userManagementApi';

interface UserPaymentDetailsCardProps {
  paymentDetails: UserPaymentDetailsDto;
}

// Masking utilities following mobile pattern
const maskAccountNumber = (accountNumber: string | null): string => {
  if (!accountNumber || accountNumber.length < 4) return '-';
  return `••••${accountNumber.slice(-4)}`;
};

const maskSortCode = (sortCode: string | null): string => {
  if (!sortCode || sortCode.length < 2) return '-';
  return `••-••-${sortCode.slice(-2)}`;
};

const formatSortCode = (sortCode: string | null): string => {
  if (!sortCode || sortCode.length !== 6) return sortCode || '-';
  return `${sortCode.slice(0, 2)}-${sortCode.slice(2, 4)}-${sortCode.slice(4, 6)}`;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export function UserPaymentDetailsCard({
  paymentDetails,
}: UserPaymentDetailsCardProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = useCallback(
    async (value: string | null, fieldName: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 3000);
      } catch {
        // Silently fail
      }
    },
    []
  );

  const { bankDetails, address, cscsCard, dateOfBirth, isComplete } =
    paymentDetails;

  const hasBankDetails = bankDetails?.accountNumber || bankDetails?.sortCode;
  const hasAddress = address?.line1;
  const hasCscsCard = cscsCard?.cardNumber;

  return (
    <Card variant="outlined" data-testid="user-payment-details-card">
      <CardContent>
        <Stack spacing={2}>
          {/* Header with reveal toggle */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon fontSize="small" color="action" />
              <Typography variant="h6" component="h3">
                Payment Details
              </Typography>
            </Box>
            {hasBankDetails && (
              <Tooltip
                title={isRevealed ? 'Hide bank details' : 'Reveal bank details'}
              >
                <IconButton
                  onClick={() => setIsRevealed(!isRevealed)}
                  size="small"
                  data-testid="toggle-reveal-button"
                >
                  {isRevealed ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Bank Details Section */}
          {hasBankDetails && (
            <>
              <Typography
                variant="subtitle2"
                color="text.primary"
                fontWeight={600}
                sx={{ mt: 1 }}
              >
                Bank Account
              </Typography>
              <Stack spacing={1.5}>
                {bankDetails?.accountNumber && (
                  <CopyableDetailRow
                    label="Account Number"
                    value={
                      isRevealed
                        ? bankDetails.accountNumber
                        : maskAccountNumber(bankDetails.accountNumber)
                    }
                    rawValue={bankDetails.accountNumber}
                    canCopy={isRevealed}
                    onCopy={handleCopy}
                    copiedField={copiedField}
                    fieldName="accountNumber"
                    testId="account-number"
                  />
                )}
                {bankDetails?.sortCode && (
                  <CopyableDetailRow
                    label="Sort Code"
                    value={
                      isRevealed
                        ? formatSortCode(bankDetails.sortCode)
                        : maskSortCode(bankDetails.sortCode)
                    }
                    rawValue={bankDetails.sortCode}
                    canCopy={isRevealed}
                    onCopy={handleCopy}
                    copiedField={copiedField}
                    fieldName="sortCode"
                    testId="sort-code"
                  />
                )}
                {bankDetails?.nameOnAccount && (
                  <DetailRow
                    label="Name on Account"
                    value={bankDetails.nameOnAccount}
                    testId="name-on-account"
                  />
                )}
              </Stack>
            </>
          )}

          {/* Address Section */}
          {hasAddress && (
            <>
              <Typography
                variant="subtitle2"
                color="text.primary"
                fontWeight={600}
                sx={{ mt: 2 }}
              >
                Address
              </Typography>
              <Stack spacing={1.5}>
                {address?.line1 && (
                  <DetailRow
                    label="Address Line 1"
                    value={address.line1}
                    testId="address-line1"
                  />
                )}
                {address?.line2 && (
                  <DetailRow
                    label="Address Line 2"
                    value={address.line2}
                    testId="address-line2"
                  />
                )}
                {address?.city && (
                  <DetailRow
                    label="City"
                    value={address.city}
                    testId="address-city"
                  />
                )}
                {address?.county && (
                  <DetailRow
                    label="County"
                    value={address.county}
                    testId="address-county"
                  />
                )}
                {address?.postcode && (
                  <DetailRow
                    label="Postcode"
                    value={address.postcode}
                    testId="address-postcode"
                  />
                )}
                {address?.country && (
                  <DetailRow
                    label="Country"
                    value={address.country}
                    testId="address-country"
                  />
                )}
              </Stack>
            </>
          )}

          {/* CSCS Card Section */}
          {hasCscsCard && (
            <>
              <Typography
                variant="subtitle2"
                color="text.primary"
                fontWeight={600}
                sx={{ mt: 2 }}
              >
                CSCS Card
              </Typography>
              <Stack spacing={1.5}>
                {cscsCard?.cardNumber && (
                  <DetailRow
                    label="Card Number"
                    value={cscsCard.cardNumber}
                    testId="cscs-card-number"
                  />
                )}
                {cscsCard?.expiryDate && (
                  <DetailRow
                    label="Expiry Date"
                    value={formatDate(cscsCard.expiryDate)}
                    testId="cscs-expiry-date"
                  />
                )}
              </Stack>
            </>
          )}

          {/* Personal Info */}
          {dateOfBirth && (
            <>
              <Typography
                variant="subtitle2"
                color="text.primary"
                fontWeight={600}
                sx={{ mt: 2 }}
              >
                Personal Information
              </Typography>
              <DetailRow
                label="Date of Birth"
                value={formatDate(dateOfBirth)}
                testId="date-of-birth"
              />
            </>
          )}

          {/* Completion Status */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={isComplete ? 'Complete' : 'Incomplete'}
              size="small"
              color={isComplete ? 'success' : 'warning'}
            />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Sub-components
interface DetailRowProps {
  label: string;
  value: string;
  testId: string;
}

function DetailRow({ label, value, testId }: DetailRowProps) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} data-testid={testId}>
        {value}
      </Typography>
    </Box>
  );
}

interface CopyableDetailRowProps extends DetailRowProps {
  rawValue: string | null;
  canCopy: boolean;
  onCopy: (value: string | null, fieldName: string) => void;
  copiedField: string | null;
  fieldName: string;
}

function CopyableDetailRow({
  label,
  value,
  rawValue,
  canCopy,
  onCopy,
  copiedField,
  fieldName,
  testId,
}: CopyableDetailRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2" fontWeight={500} data-testid={testId}>
          {value}
        </Typography>
        {canCopy && rawValue && (
          <Tooltip title={copiedField === fieldName ? 'Copied!' : 'Copy'}>
            <IconButton
              size="small"
              onClick={() => onCopy(rawValue, fieldName)}
              sx={{ ml: 0.5, p: 0.25 }}
              data-testid={`copy-${fieldName}-button`}
            >
              {copiedField === fieldName ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : (
                <CopyIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
